// Component: OrdersTable.jsx
// Purpose: Reusable orders table with cancel support, status badges, source-tag badge, reconcile-state,
//          risk-flag columns, and (104 D9) user-scoped rejected-signal rows (algo + manual, classified,
//          account-attributed, with a naked-position at-risk badge) rendered alongside real orders.

import React, { useState, useCallback } from 'react';
import { getSourceTagBadge, getBrokerBadge, truncateId, resolveSourceTag, resolveProductLabel, LEG_TYPE_DISPLAY, DIAGNOSIS_STATE_LABELS, EXIT_REASON_MAP, formatExitConversion, getPriceSourceBadge, getOrderWorkingPrice, getLockStateBadge, describeOrderCharges, chargeBreakdownText } from '../../utils/tradingTransforms';
import { rejectReasonLabel, fullRejectReason, failureTypeLabel, originLabel, HISTORICAL_INCOMPLETE_LABEL } from '../../utils/rejectReason';
import { Badge, DataTable, Button } from '../ds';
import ReconcileBadge from './ReconcileBadge';
import StatusBadge from './StatusBadge';

//! R1.4 eligible set — statuses that allow cancellation (widened per 02-design.md commit C10;
//  was {PENDING, SUBMITTED} only, missed OPEN/PARTIALLY_FILLED/MONITORING which the backend
//  gate (`_CANCEL_ELIGIBLE_STATUSES`) now also accepts)
const CANCELLABLE_STATUSES = new Set(['PENDING', 'SUBMITTED', 'OPEN', 'PARTIALLY_FILLED', 'MONITORING']);

//! R2.4 eligible set — statuses that allow modify. Deliberately NO PARTIALLY_FILLED
//  (a part-filled order's remaining qty is not safely re-editable client-side; backend's
//  `_AMEND_STATUSES`-identical gate mirrors this).
const MODIFIABLE_STATUSES = new Set(['PENDING', 'SUBMITTED', 'OPEN', 'MONITORING']);

//! live-exit-lane R9 (W6) — a chase ladder / diagnosis annotation only makes sense on an order
//  still in flight. Mirrors StatusBadge.jsx's STATUS_VARIANT vocabulary (FILLED/REJECTED/
//  CANCELLED are the only statuses that map there today) — the complement of that set is
//  "non-terminal" for badge purposes. Kept local (not exported) since no other file needs it yet.
const TERMINAL_STATUSES = new Set(['FILLED', 'REJECTED', 'CANCELLED', 'CLOSED', 'EXPIRED']);

// --> Price formatter (en-IN locale, 2 decimal places)
const formatPrice = (val) => {
  if (val == null) return '—';
  const num = typeof val === 'number' ? val : parseFloat(val);
  if (isNaN(num)) return '—';
  return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// --> Format _epoch timestamp to IST HH:MM:SS (today) or "DD Mon HH:MM:SS" (non-today).
//! Mirrors OrderDetailDrawer: a bare time is unauditable on NSE/BSE — you can't tell which
//  trading day. For orders NOT placed on the current IST date we prefix the date so the
//  Orders table is self-describing. Today's orders stay time-only to keep the column compact.
const _istTimeFmt = new Intl.DateTimeFormat('en-IN', {
  timeZone: 'Asia/Kolkata',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

const _istDateTimeFmt = new Intl.DateTimeFormat('en-IN', {
  timeZone: 'Asia/Kolkata',
  day: '2-digit',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

// --> IST calendar date key (YYYY-MM-DD) for a given Date, for same-day comparison.
const _istDateKeyFmt = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Kolkata',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const _istDateKey = (d) => _istDateKeyFmt.format(d);

const formatTime = (epochMs) => {
  if (!epochMs) return '—';
  try {
    const d = new Date(epochMs);
    if (isNaN(d.getTime())) return '—';
    //! Same IST calendar day → time-only; otherwise prefix the date (DD Mon HH:MM:SS).
    if (_istDateKey(d) === _istDateKey(new Date())) {
      return _istTimeFmt.format(d);
    }
    return _istDateTimeFmt.format(d);
  } catch {
    return '—';
  }
};

/*>>> SUB-COMPONENTS <<<*/

// --> PAPER/LIVE execution-mode badge (R106.8) — trading mode, distinct from source_tag
//* 104 Element R (R12.5) — exported so StuckOrdersPanel.jsx reuses the SAME badge instead
//* of forking a second paper/live indicator (feedback_reuse_discovery_before_patching_loopbacks).
export const ModeBadge = ({ mode }) => (
  <Badge variant={mode === 'LIVE' ? 'profit' : mode === 'UNATTRIBUTED' ? 'neutral' : 'info'} size="sm">
    {mode === 'LIVE' ? 'Live' : mode === 'UNATTRIBUTED' ? 'UNATTRIBUTED' : 'Paper'}
  </Badge>
);

// --> Source-tag badge (R102.3) — FOREIGN never rendered (screened at endpoint)
const SourceTagBadge = ({ tag }) => {
  const { label, variant } = getSourceTagBadge(tag);
  return <Badge variant={variant} size="sm">{label}</Badge>;
};

// --> Slippage cell
const SlippageCell = ({ value }) => {
  if (value == null) return <span className="text-fg-2">—</span>;
  if (value === 0) return <span className="font-mono text-xs text-profit">0.00</span>;
  if (value > 0) return <span className="font-mono text-xs text-warning">+{value.toFixed(2)}</span>;
  return <span className="font-mono text-xs text-profit">{value.toFixed(2)}</span>;
};

// --> 144-6 R7-T3 — Charges cell: the API charges_total (breakdown in the tooltip, Indicative when
//     schedule-derived) or an explicit "Unavailable" with the reason. Never blank, never a silent 0.
const ChargesCell = ({ order }) => {
  const d = describeOrderCharges(order);
  if (d.state === 'none') return <span className="text-fg-2">—</span>;
  if (d.state === 'unavailable') {
    return (
      <span className="text-xs italic text-fg-2" title={d.reason}>
        Unavailable{d.reasonPhrase ? ` (${d.reasonPhrase})` : ''}
      </span>
    );
  }
  return (
    <span className="font-mono text-sm tabular-nums text-fg-1" title={chargeBreakdownText(order) || undefined}>
      ₹{formatPrice(d.total)}
      {d.indicative && <span className="ml-1 text-[10px] text-fg-2">Indicative</span>}
    </span>
  );
};

// --> Cancel button (DS Button, danger/sm). R5.2 — disables while a cancel is in flight
//     (double-click guard) OR while this order already has an unresolved corrective (R5.3).
const CancelButton = ({ orderId, onCancel, pending }) => (
  <Button
    variant="danger"
    size="sm"
    onClick={(e) => { e.stopPropagation(); onCancel(orderId); }}
    disabled={pending}
    aria-label={`Cancel order ${orderId}`}
  >
    {pending ? 'Cancelling…' : 'Cancel'}
  </Button>
);

// --> Modify button (DS Button, secondary/sm). R6.1 — opens ModifyOrderModal for this order.
const ModifyButton = ({ order, onModify, disabled }) => (
  <Button
    variant="secondary"
    size="sm"
    onClick={(e) => { e.stopPropagation(); onModify(order); }}
    disabled={disabled}
    aria-label={`Modify order ${order.order_id ?? order.orderId}`}
  >
    Modify
  </Button>
);

// --> R5.3/R6.4 — an unresolved cancel/modify is durably tracked server-side
//     (`pending_corrective_resolutions`, R8.1) and surfaced via `OrderResponse.pending_corrective_type`.
//     Rendering this badge instead of a bare unchanged status is the fix for the "silent
//     no-feedback state" the data-lineage pass flagged (02-design.md Data lineage §3).
const PendingCorrectiveBadge = ({ type }) => {
  if (!type) return null;
  const label = type === 'CANCEL' ? 'Cancel pending…' : 'Modify pending…';
  return (
    <Badge
      variant="warn"
      size="sm"
      title="Awaiting broker confirmation"
    >
      {label}
    </Badge>
  );
};

// --> 104 D9 — naked-position at-risk badge. Rendered only when a rejection row's
//     naked_position_active === true (typically failure_type === 'protective_leg_failure_naked').
const AtRiskBadge = () => (
  <Badge
    variant="loss"
    size="sm"
    title="Protective leg failed — this position may be naked. Manual intervention may be required."
  >
    AT RISK
  </Badge>
);

// --> Risk-flag warning icon (R102.4) — only rendered when row.risk_flag === true
const RiskFlagIcon = ({ passivatedAt }) => {
  const tooltip = passivatedAt
    ? `Manual-vs-algo conflict detected\n\nConflictMonitor detected a manual trade conflicting with an active algo position on this instrument. Review before placing further orders.\n\nPassivated at: ${passivatedAt}`
    : 'Manual-vs-algo conflict detected\n\nConflictMonitor detected a manual trade conflicting with an active algo position on this instrument. Review before placing further orders.';
  return (
    <span
      className="inline-flex items-center justify-center rounded-full bg-warning/15 border border-warning/20 text-warning w-5 h-5 text-[12px] cursor-default"
      aria-label="Conflict risk flag"
      title={tooltip}
      role="img"
    >
      ⚠
    </span>
  );
};

/*>>> COLUMN DEFINITIONS <<<*/

// Full columns (order matters)
const FULL_COLUMNS = [
  { key: 'order_id',        label: 'Order ID' },
  { key: 'strategy',        label: 'Strategy' },
  { key: 'source_tag',      label: 'Source' },        // R102.3 — source-tag badge
  { key: 'execution_mode',  label: 'Mode' },          // R106.8 — PAPER/LIVE badge
  { key: 'broker',          label: 'Broker' },        // R3.2 — per-broker badge (Flattrade/ICICI/Paper)
  { key: 'instrument',      label: 'Instrument' },    // D1 — symbol/exchange; never UUID
  { key: 'side',            label: 'Side' },
  { key: 'leg',             label: 'Leg' },           // manual-exit-netting-p0 R3.2 — entry/exit mark
  { key: 'type',            label: 'Type' },
  { key: 'qty',             label: 'Qty',             align: 'right' },
  { key: 'price',           label: 'Price',           align: 'right' },
  { key: 'filledPrice',     label: 'Filled Price',    align: 'right' },
  { key: 'slippage',        label: 'Slippage',        align: 'right' },
  { key: 'charges',         label: 'Charges',         align: 'right' },   // 144-6 R7-T3 — per-order charges (number or reason)
  { key: 'status',          label: 'Status' },
  { key: 'reconcile_state', label: 'Reconcile' },     // R104.5 — reconcile-state badge
  { key: 'action',          label: 'Action' },
  { key: '_epoch',          label: 'Time' },
];

// Compact columns
const COMPACT_COLUMNS = [
  { key: 'source_tag',      label: 'Source' },        // R102.3
  { key: 'execution_mode',  label: 'Mode' },          // R106.8 — PAPER/LIVE badge
  { key: 'broker',          label: 'Broker' },        // R3.2 — per-broker badge
  { key: 'instrument',      label: 'Instrument' },    // D1 — symbol/exchange; never UUID
  { key: 'side',            label: 'Side' },
  { key: 'leg',             label: 'Leg' },           // manual-exit-netting-p0 R3.2 — entry/exit mark
  { key: 'type',            label: 'Type' },
  { key: 'qty',             label: 'Qty',          align: 'right' },
  { key: 'filledPrice',     label: 'Filled Price', align: 'right' },
  { key: 'charges',         label: 'Charges',      align: 'right' },   // 144-6 R7-T3
  { key: 'status',          label: 'Status' },
  { key: 'reconcile_state', label: 'Reconcile' },     // R104.5
  { key: 'action',          label: 'Action' },
  { key: '_epoch',          label: 'Time' },
];

/*>>> MAIN COMPONENT <<<*/

/**
 * OrdersTable — reusable orders table with cancel support.
 *
 * @param {Object[]} orders          - Array of order objects (from OMS/WS events)
 * @param {Object[]} rejections      - 104 D9 — array of rejected_signals rows (GET /orders/rejected,
 *                                     user-scoped mode). Rendered as distinguishable REJECTED rows
 *                                     interleaved (sorted) with `orders`, never a separate table —
 *                                     a rejection is a row that never became an order.
 * @param {boolean}  compact         - Fewer columns for embedded views (default: false)
 * @param {boolean}  showStrategy    - Show strategy name column (default: true)
 * @param {string}   maxHeight       - Scrollable height CSS value (default: none)
 * @param {Function} onCancel        - Cancel handler: (orderId) => void|Promise (R5.1/R5.2.1 — caller
 *                                     owns the confirm dialog + network call; resolve/reject settles
 *                                     the row's disabled-while-pending state, R5.2)
 * @param {Function} onModify        - Modify handler: (order) => void — opens the ModifyOrderModal (R6.1)
 */
const OrdersTable = ({
  orders = [],
  rejections = [],
  compact = false,
  showStrategy = true,
  maxHeight,
  onCancel,
  onModify,
  onRowClick,
}) => {
  /*>>> STATE <<<*/

  //! R5.2 — double-click guard: order_ids with a cancel currently in flight (from click
  //  until the caller-supplied onCancel promise settles, which spans the confirm dialog +
  //  the network round trip). Local to this component so no parent wiring is required just
  //  to disable a button.
  const [pendingCancelIds, setPendingCancelIds] = useState(() => new Set());

  const handleCancelClick = useCallback((orderId) => {
    if (!onCancel) return;
    setPendingCancelIds((prev) => {
      const next = new Set(prev);
      next.add(orderId);
      return next;
    });
    Promise.resolve(onCancel(orderId)).finally(() => {
      setPendingCancelIds((prev) => {
        if (!prev.has(orderId)) return prev;
        const next = new Set(prev);
        next.delete(orderId);
        return next;
      });
    });
  }, [onCancel]);

  /*>>> DERIVED STATE <<<*/

  // --> Pick column set, then filter out Strategy column if not needed
  const baseColumns = compact ? COMPACT_COLUMNS : FULL_COLUMNS;
  const columns = baseColumns.filter((col) => {
    if (col.key === 'strategy' && !showStrategy) return false;
    return true;
  });

  // --> 104 D9 — normalize rejections into row shape (tag `_isRejection: true`, derive `_epoch`
  //     from created_at the same way the windowed-order fetch does) and merge with real orders
  //     into ONE chronologically-sorted set — a rejection is a row that never became an order,
  //     shown together with normal orders on this SAME Orders page, not a separate surface.
  const rejectionRows = (rejections || []).map((r) => ({
    ...r,
    _isRejection: true,
    _epoch: r.created_at ? new Date(r.created_at).getTime() : 0,
  }));
  const combinedRows = [
    ...orders.map((o) => ({ ...o, _isRejection: false })),
    ...rejectionRows,
  ];

  // --> Sort by _epoch descending (newest first)
  const sortedOrders = [...combinedRows].sort((a, b) => {
    const aEpoch = a._epoch ?? 0;
    const bEpoch = b._epoch ?? 0;
    return bEpoch - aEpoch;
  });

  /*>>> EMPTY STATE <<<*/
  if (sortedOrders.length === 0) {
    return (
      <div className="overflow-hidden rounded-xl border border-[var(--divider)] bg-bg-2">
        <div className="flex items-center justify-center py-12 text-center">
          <span className="text-sm text-fg-2">No orders yet</span>
        </div>
      </div>
    );
  }

  /*>>> CELL RENDERER <<<*/
  const renderCell = (col, order) => {
    switch (col.key) {
      // Order ID — stacked: DeeTrade UUID (truncated) on top, broker id (BRK) sub-label below.
      // Collapse rules: id===broker_id → single broker line; broker_id absent → UUID only.
      case 'order_id': {
        const oid = order.order_id ?? order.orderId ?? null;
        const bid = order.broker_order_id ?? null;
        if (!oid && !bid) return <span className="font-mono text-xs text-fg-2">—</span>;
        if (bid && oid === bid) {
          return <span className="font-mono text-xs text-fg-1" title={bid}>{bid}</span>;
        }
        // oid is always present for a canonical DeeTrade order; the oid-absent+bid-present path
        // (single "BRK <id>" line) is not expected in practice.
        return (
          <div className="flex flex-col">
            {oid && (
              <span className="font-mono text-xs text-fg-1" title={oid}>{truncateId(oid)}</span>
            )}
            {bid && (
              <span className="font-mono text-[10px] text-fg-2" title={bid}>BRK {bid}</span>
            )}
          </div>
        );
      }

      // Strategy name
      case 'strategy':
        return (
          <span className="text-sm font-medium text-fg-1">
            {order.strategy ?? '—'}
          </span>
        );

      // Instrument — D1: symbol (bold) + exchange sub-label. NEVER renders instrument_id UUID.
      // instrument_id may be placed in a tooltip only; visible text must be symbol/name or '—'.
      case 'instrument': {
        // order-instrument-identity-normalization P2b — prefer the stored display_symbol
        // (canonical instrument-master name), then symbol (#4b read-lane resolve), then name;
        // never the UUID.
        const sym = order.display_symbol ?? order.symbol ?? order.name ?? null;
        const exch = order.exchange ?? null;
        if (!sym) return <span className="text-sm text-fg-2">—</span>;
        return (
          <div className="flex flex-col" title={exch ? `${sym} · ${exch}` : sym}>
            <span className="text-sm font-semibold text-fg-1">{sym}</span>
            {exch && (
              <span className="text-[10px] text-fg-2">{exch}</span>
            )}
          </div>
        );
      }

      // Side (BUY/SELL)
      case 'side':
        return (
          <span className={`text-sm font-semibold ${order.side === 'BUY' ? 'text-profit' : 'text-loss'}`}>
            {order.side ?? '—'}
          </span>
        );

      // Leg mark (Entry / Manual Exit / Corrective / Stop Loss / ...) — manual-exit-netting-p0 R3.2.
      // Reuses the drawer's LEG_TYPE_DISPLAY vocabulary (no divergent second label set). Falls back
      // to the RAW string if a leg_type has no mapping (never blank/undefined) — the drift-lock
      // test (tradingTransforms.legTypeCoverage.test.js) is what prevents that fallback in practice.
      case 'leg': {
        //! live-exit-lane R9 (W6) — MINIMAL chase/diagnosis surface, deliberately NOT a full
        //  color-coded diagnosis-state column (out of this lane's scope, 02-design.md §8). Badge
        //  renders only when chase_attempt_count > 0 AND status is non-terminal; the diagnosis
        //  label rides the SAME badge's title= tooltip rather than a second element, keeping the
        //  leg cell's footprint near-zero. Both fields may be absent/null (getattr-guarded on the
        //  backend) — absence renders nothing, never "undefined"/"null".
        const statusUpper = (order.status ?? '').toUpperCase();
        const chaseAttempt = order.chase_attempt_count;
        const showChaseBadge = typeof chaseAttempt === 'number' && chaseAttempt > 0 && !TERMINAL_STATUSES.has(statusUpper);
        const diagnosisLabel = order.diagnosis_state
          ? (DIAGNOSIS_STATE_LABELS[order.diagnosis_state] ?? order.diagnosis_state)
          : null;
        //! N (the chase ceiling) — QA fix-wave-2 update (2026-07-24): the BACKEND gap this
        //  comment previously documented is closed — `fill_expectation_watchdog.py`'s
        //  `_resolve_chase_policy` now reads the R7.2 per-deployment `chase_n` override (via
        //  `AccountFanoutPlanner.get_cached_policy_overrides`, `Order.policy_snapshot` fallback)
        //  BEFORE falling back to the platform-default `CHASE_LADDER_N_ATTEMPTS` module constant
        //  — a deployment with a saved override now genuinely chases to N attempts, not always 3.
        //  The REMAINING gap is FE-only: there is still no per-row passthrough field for the
        //  RESOLVED N (02-design.md §8's 5 ConsolidatedRow fields are exit_reason/target_stage/
        //  diagnosis_state/chase_attempt_count/variety — no resolved-cap field), so this badge
        //  cannot render the order's OWN deployment's actual ceiling and still shows the platform
        //  default as a best-effort, honestly-labelled approximation. Add a per-row
        //  `resolved_chase_n` field (or equivalent) the day this FE gap is prioritized.
        const CHASE_LADDER_N_ATTEMPTS_DEFAULT = 3; // fill_expectation_watchdog.py:135 (platform default only)
        const chaseCap = CHASE_LADDER_N_ATTEMPTS_DEFAULT;

        //--> 10509-12 E13 / R10 — prefer the audit-derived conversion label when present.
        const legLabel = formatExitConversion(order)
          || (order.leg_type ? (LEG_TYPE_DISPLAY[order.leg_type] ?? order.leg_type) : null);
        //--> algo-entry-null-limit-price D2 — price-source provenance badge (SEBI audit
        //    distinguishability: manual / LTP+off / OB bid / OB ask / band-clamped; entry-derived
        //    vs exit-derived reconstructable via the `entry_*` price_source prefix, operator
        //    decision 3). getPriceSourceBadge ALWAYS resolves to a badge (NULL/undefined
        //    price_source -> "Price: Manual") — so this cell is no longer ever fully blank, which
        //    is why the old `if (!order.leg_type && !showChaseBadge) return —` early-return above
        //    is removed: every order now shows at least this attribution.
        //! Stage-4 QA A/B loop-back (iter-2 BLOCKER 2) — this badge's label set (PRICE_SOURCE_BADGE
        //! / PRICE_SOURCE_MANUAL_BADGE in tradingTransforms.js) is deliberately qualified (`Price:`/
        //! `Px:` prefixes) so it can NEVER read as "Manual" identically to the order-source badge
        //! rendered in this SAME row's `source_tag` column (SourceTagBadge, `case 'source_tag':`
        //! below) — the two
        //! previously-indistinguishable "Manual" badges were the exact defect QA caught.
        const priceBadge = getPriceSourceBadge(order.price_source, order.band_clamped);
        //--> R4 defense-in-depth surfacing — the placing user sees their MARKET was silently
        //    rewritten to a priced LIMIT (capability-gated broker + OMS converter), never a
        //    silent substitution.
        const wasMarket = order.original_order_type === 'MARKET';
        return (
          <div className="flex items-center gap-1.5 flex-wrap">
            {legLabel && <Badge variant="neutral" size="sm">{legLabel}</Badge>}
            <Badge
              variant={priceBadge.variant}
              size="sm"
              //--> Stage-4 QA A/B loop-back — always-present title (not just when clamped) so a
              //    hover makes explicit this is the PRICE-derivation badge, further disambiguating
              //    it from the order-source badge in this row's separate `source_tag` column.
              title={priceBadge.clamped && priceBadge.sourceLabel ? `Clamped from ${priceBadge.sourceLabel}` : 'Price-derivation source'}
            >
              {priceBadge.label}
            </Badge>
            {wasMarket && (
              <span
                className="text-[9px] font-semibold text-warning uppercase tracking-wide"
                title="Placed as MARKET — the broker doesn't support it for this instrument; converted to a priced LIMIT"
              >
                was: MARKET
              </span>
            )}
            {showChaseBadge && (
              <Badge
                variant="warn"
                size="sm"
                title={diagnosisLabel ?? undefined}
              >
                {`chase ${chaseAttempt}/${chaseCap ?? '—'}`}
              </Badge>
            )}
          </div>
        );
      }

      // Order type (MARKET/LIMIT/SL-M...) + product sub-label (Delivery/Intraday)
      // — mirrors the instrument cell's stacked main/sub pattern.
      case 'type': {
        const productLabel = resolveProductLabel(order.product_type);
        return (
          <div className="flex flex-col">
            <span className="text-sm text-fg-1">
              {order.type ?? order.order_type ?? '—'}
            </span>
            {productLabel && (
              <span className="text-[10px] text-fg-2">{productLabel}</span>
            )}
          </div>
        );
      }

      // Quantity
      case 'qty':
        return (
          <span className="font-mono text-sm tabular-nums text-fg-1">
            {order.qty ?? '—'}
          </span>
        );

      // Limit / trigger price
      case 'price':
        return (
          <span className="font-mono text-sm tabular-nums text-fg-1">
            {formatPrice(getOrderWorkingPrice(order))}
          </span>
        );

      // Filled price
      case 'filledPrice':
        return (
          <span className="font-mono text-sm tabular-nums text-fg-1">
            {formatPrice(order.filledPrice ?? order.filled_price)}
          </span>
        );

      // Slippage
      case 'slippage':
        return <SlippageCell value={order.slippage} />;

      // 144-6 R7-T3 — Charges (derived at read from the one charges authority)
      case 'charges':
        return <ChargesCell order={order} />;

      // Status badge
      case 'status': {
        const status = (order.status ?? '').toUpperCase();
        //! R1.2 — a REJECTED order shows the broker's own human reason under the badge (mirrors
        //!  TradeCard.jsx:334). rejectReasonLabel returns null for non-rejected / reasonless rows,
        //!  so the sub-label renders nothing (never the literal "null"/"undefined").
        //! #102 R21 — a CANCELLED order ALSO shows this sub-label when reject_reason is
        //!  non-empty: a live OCO protective leg's sibling is cancelled by the OMS with the
        //!  cancel reason (e.g. "oco_sibling_filled") stamped onto reject_reason, not
        //!  exit_reason — without this a CANCELLED OCO sibling shows a bare, unexplained
        //!  "Cancelled" badge. An ordinary CANCELLED row with no reject_reason still renders
        //!  nothing (rejectReasonLabel(null) === null).
        const reason =
          status === 'REJECTED' || status === 'CANCELLED'
            ? rejectReasonLabel(fullRejectReason(order))
            : null;
        //! F-12 (QA fix-wave, live-exit-lane R9) — a CANCELLED/closed exit leg carrying
        //!  `exit_reason` shows the SAME human label EXIT_REASON_MAP already resolves on the
        //!  trade-history surface (TradeCard.jsx) — reused verbatim here, never a second
        //!  divergent copy. Mutually exclusive with the REJECTED sub-label above (an
        //!  exit_reason is only ever set on a closed/cancelled leg, never a rejected one).
        //!  Unmapped/absent exit_reason renders nothing here — StatusBadge's own "Cancelled"
        //!  text still shows; this sub-label only ADDS attribution, never replaces the badge.
        const exitReasonLabel = (
          status !== 'REJECTED' && order.exit_reason
            ? (EXIT_REASON_MAP[order.exit_reason] || null)
            : null
        );
        //! 10509-6/E13/D31 — SL row "BE"/"Locked" chip, display-only, next to the status badge.
        //  order.lock_state passes through untouched (OrdersTable spreads the raw row — no
        //  whitelist to extend here); getLockStateBadge(null/absent) returns null → no chip.
        const lockBadge = getLockStateBadge(order.lock_state);
        return (
          <div className="flex flex-col">
            <span className="flex items-center gap-1">
              <StatusBadge status={status} display_state={order.display_state} />
              {lockBadge && (
                <Badge variant={lockBadge.variant} size="sm" data-testid="lock-state-badge">
                  {lockBadge.label}
                </Badge>
              )}
            </span>
            {reason && <span className="text-[10px] text-fg-2 mt-0.5">{reason}</span>}
            {exitReasonLabel && (
              <span className="text-[10px] text-fg-2 mt-0.5" data-testid="exit-reason-label">
                {exitReasonLabel}
              </span>
            )}
          </div>
        );
      }

      // Source-tag badge (R102.3) — FOREIGN never rendered; lean-WS falls back to `source`
      case 'source_tag':
        return <SourceTagBadge tag={resolveSourceTag(order)} />;

      // PAPER/LIVE execution-mode badge (R106.8)
      case 'execution_mode': {
        const mode = (order.execution_mode ?? '').toLowerCase();
        if (mode === 'paper') return <ModeBadge mode="PAPER" />;
        if (mode === 'live')  return <ModeBadge mode="LIVE" />;
        // R8.4/R8.5 — server-derived 3-way label; never re-derive client-side
        if (mode === 'unattributed') return <ModeBadge mode="UNATTRIBUTED" />;
        return <span className="text-sm text-fg-2">—</span>;  // defensive-only (field rarely null)
      }

      // Per-broker badge (R3.2) — Flattrade/ICICI/Paper; unmapped/paper-null → em-dash, never a broken badge
      case 'broker': {
        const cfg = getBrokerBadge(order.broker);
        return cfg
          ? <Badge variant={cfg.variant} size="sm">{cfg.label}</Badge>
          : <span className="text-sm text-fg-2">—</span>;
      }

      // Reconcile-state badge + tooltip (R104.5)
      case 'reconcile_state':
        return <ReconcileBadge state={order.reconcile_state} />;

      // Signal action label (entry, sl, tp, exit) + Cancel/Modify actions
      case 'action': {
        const orderId = order.order_id ?? order.orderId;
        const statusUpper = (order.status ?? '').toUpperCase();
        //! R5.3/R6.4 — an order with an unresolved corrective in flight (AMBIGUOUS broker
        //  result, still being resolved by PendingCorrectiveResolver) disables BOTH actions —
        //  a second cancel/modify while one is already durably pending would race the resolver.
        const pendingCorrective = order.pending_corrective_type ?? null;
        const actionsDisabled = Boolean(pendingCorrective);
        return (
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs text-fg-2">
              {order.action ?? order.signal ?? '—'}
            </span>
            {/* --> Risk-flag warning icon (R102.4) */}
            {order.risk_flag === true && (
              <RiskFlagIcon passivatedAt={order.passivated_at ?? null} />
            )}
            {/* --> Pending-corrective badge (R5.3) — replaces a silently-unchanged status */}
            <PendingCorrectiveBadge type={pendingCorrective} />
            {/* --> Cancel button for cancellable statuses */}
            {onCancel && CANCELLABLE_STATUSES.has(statusUpper) && (
              <CancelButton
                orderId={orderId}
                onCancel={handleCancelClick}
                pending={pendingCancelIds.has(orderId) || actionsDisabled}
              />
            )}
            {/* --> Modify button for modify-eligible statuses (R6.1) */}
            {onModify && MODIFIABLE_STATUSES.has(statusUpper) && (
              <ModifyButton
                order={order}
                onModify={onModify}
                disabled={actionsDisabled}
              />
            )}
          </div>
        );
      }

      // Timestamp
      //! Render the ORDER's real time (created_at / order_timestamp), NOT _epoch.
      //! _epoch is computed inconsistently across context paths (one path falls back to
      //! Date.now() = page-load time when the row carries created_at but no `timestamp`),
      //! which made every broker order show the same load-time. created_at is the canonical
      //! order timestamp from the broker; fall back to _epoch only if it's missing.
      case '_epoch': {
        const _tsMs =
          (order.created_at && new Date(order.created_at).getTime()) ||
          (order.order_timestamp && new Date(order.order_timestamp).getTime()) ||
          order._epoch;
        return (
          <span className="font-mono text-xs tabular-nums text-fg-2">
            {formatTime(_tsMs)}
          </span>
        );
      }

      default:
        return null;
    }
  };

  /*>>> REJECTION CELL RENDERER (104 D9) <<<*/
  //  A rejected_signals row (GET /orders/rejected, user-scoped) never has an order_id, symbol,
  //  broker fill data, etc. — it is rendered through its OWN cell mapping per column, reusing
  //  the SAME column layout (and the SAME StatusBadge/rejectReasonLabel machinery the normal
  //  REJECTED-order status cell already uses) so it slots into the table as a distinguishable
  //  row rather than a bolted-on second table.
  const renderRejectionCell = (col, rej) => {
    switch (col.key) {
      case 'order_id': {
        const sid = rej.signal_id ?? null;
        return sid
          ? <span className="font-mono text-xs text-fg-1" title={sid}>{truncateId(sid)}</span>
          : <span className="font-mono text-xs text-fg-2">—</span>;
      }

      case 'strategy':
        return <span className="text-sm text-fg-2">—</span>;

      // origin (internal/broker) badge — reuses originLabel, kept in the source_tag column
      // so a rejection's "who classified this" reads in the same visual slot as a real
      // order's source-tag badge.
      case 'source_tag': {
        const label = originLabel(rej.origin);
        return label
          ? <Badge variant="neutral" size="sm">{label}</Badge>
          : <span className="text-sm text-fg-2">—</span>;
      }

      // execution_mode arrives from the endpoint as an already-human display label — render
      // verbatim (never re-derive a PAPER/LIVE badge client-side for a rejection row).
      case 'execution_mode':
        return rej.execution_mode
          ? <Badge variant="neutral" size="sm">{rej.execution_mode}</Badge>
          : <span className="text-sm text-fg-2">—</span>;

      case 'broker':
        return <span className="text-sm text-fg-2">—</span>;

      // D1 — a rejected_signals row only carries instrument_id (no display_symbol); never
      // render the raw UUID on this human surface. Full instrument_id is available in the drawer.
      case 'instrument':
        return <span className="text-sm text-fg-2">—</span>;

      case 'side':
        return rej.side
          ? <span className={`text-sm font-semibold ${rej.side === 'BUY' ? 'text-profit' : 'text-loss'}`}>{rej.side}</span>
          : <span className="text-sm text-fg-2">—</span>;

      // failure_type + naked-at-risk badge — the two most safety-critical classification
      // signals get the most visually prominent slot (same slot as the badge-heavy Leg column).
      case 'leg': {
        const failureLabel = failureTypeLabel(rej.failure_type);
        return (
          <div className="flex items-center gap-1.5 flex-wrap">
            {failureLabel && (
              <Badge variant="warn" size="sm" title={rej.failure_type ?? undefined}>{failureLabel}</Badge>
            )}
            {rej.naked_position_active === true && <AtRiskBadge />}
          </div>
        );
      }

      case 'type':
        return <span className="text-sm text-fg-2">—</span>;

      case 'qty':
        return <span className="font-mono text-sm tabular-nums text-fg-1">{rej.qty ?? '—'}</span>;

      case 'price':
        return <span className="font-mono text-sm tabular-nums text-fg-1">{formatPrice(rej.price)}</span>;

      case 'filledPrice':
        return <span className="text-sm text-fg-2">—</span>;

      case 'slippage':
        return <span className="text-fg-2">—</span>;

      case 'charges':
        return <span className="text-fg-2">—</span>;

      // REJECTED badge + reject_reason (humanized) — historical_incomplete rows show the
      // HISTORICAL_INCOMPLETE_LABEL instead of a blank/absent reason (R2.7-equivalent for D9).
      case 'status': {
        const reason = rej.historical_incomplete === true
          ? HISTORICAL_INCOMPLETE_LABEL
          : rejectReasonLabel(fullRejectReason(rej));
        return (
          <div className="flex flex-col">
            <StatusBadge status="REJECTED" />
            {reason && <span className="text-[10px] text-fg-2 mt-0.5">{reason}</span>}
            {rej.sub_reason && <span className="text-[10px] text-fg-2 mt-0.5">{rej.sub_reason}</span>}
          </div>
        );
      }

      case 'reconcile_state':
        return <span className="text-sm text-fg-2">—</span>;

      // account attribution — a rejection is fanned out per-account server-side (each row already
      // carries exactly one account_id), so this cell just surfaces the resolved label.
      case 'action': {
        const acctLabel = rej.account_label ?? rej.account_id ?? '—';
        return (
          <span className="text-xs text-fg-2" data-testid="rejection-account-label">
            {acctLabel}
          </span>
        );
      }

      case '_epoch': {
        const tsMs = (rej.created_at && new Date(rej.created_at).getTime()) || rej._epoch;
        return (
          <span className="font-mono text-xs tabular-nums text-fg-2">
            {formatTime(tsMs)}
          </span>
        );
      }

      default:
        return null;
    }
  };

  /*>>> RENDER <<<*/
  return (
    <div className="overflow-hidden rounded-xl border border-[var(--divider)] bg-bg-2">
      <div
        className="overflow-x-auto"
        style={maxHeight ? { maxHeight, overflowY: 'auto' } : undefined}
      >
        <DataTable className="w-full">
          {/* --> Table header */}
          <DataTable.Head>
            <DataTable.Row>
              {columns.map((col) => (
                <DataTable.HeaderCell
                  key={col.key}
                  align={col.align || 'left'}
                >
                  {col.label}
                </DataTable.HeaderCell>
              ))}
            </DataTable.Row>
          </DataTable.Head>

          {/* --> Table body */}
          <DataTable.Body>
            {sortedOrders.map((order, idx) => {
              const key = order._isRejection
                ? `rejection-${order.signal_id ?? idx}-${order.account_id ?? 'noacct'}`
                : (order.order_id ?? order.orderId ?? `order-${idx}`);
              return (
                <DataTable.Row
                  key={key}
                  data-testid={order._isRejection ? 'rejection-row' : undefined}
                  onClick={onRowClick ? () => onRowClick(order) : undefined}
                  //! 104 D9 — a rejection row is visually distinguishable (subtle loss-tinted
                  //  background) from a real order row, in addition to its own REJECTED badge.
                  className={[
                    onRowClick ? 'cursor-pointer' : '',
                    order._isRejection ? 'bg-loss/5' : '',
                  ].filter(Boolean).join(' ') || undefined}
                  title={onRowClick ? 'View order detail' : undefined}
                >
                  {columns.map((col) => (
                    <DataTable.Cell
                      key={col.key}
                      align={col.align}
                    >
                      {order._isRejection ? renderRejectionCell(col, order) : renderCell(col, order)}
                    </DataTable.Cell>
                  ))}
                </DataTable.Row>
              );
            })}
          </DataTable.Body>
        </DataTable>
      </div>

      {/* --> Footer */}
      <div className="flex items-center border-t border-[var(--divider)] bg-bg-2 px-4 py-2">
        <span className="text-xs text-fg-2">
          Showing {sortedOrders.length} {sortedOrders.length === 1 ? 'order' : 'orders'}
        </span>
      </div>
    </div>
  );
};

export default OrdersTable;
